import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Store, Trash2, Plus, Minus, ShoppingCart, CreditCard, BadgeCheck } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Loader, EmptyState } from "../components/Loader";
import { PageHeader } from "../components/PageHeader";

export default function Cart() {
  const navigate = useNavigate();
  const { refresh: refreshBadge } = useCart();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try { setData(await api("/cart")); } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateQty = async (item, delta) => {
    setBusy(item.item_id);
    try { const fresh = await api(`/cart/items/${item.item_id}`, { method: "PUT", body: JSON.stringify({ quantity: item.quantity + delta }) }); setData(fresh); refreshBadge(); }
    catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  const remove = async (item) => {
    setBusy(item.item_id);
    try { const fresh = await api(`/cart/items/${item.item_id}`, { method: "DELETE" }); setData(fresh); refreshBadge(); }
    catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  const clearAll = async () => {
    if (!window.confirm("حذف كل عناصر السلة؟")) return;
    try { const fresh = await api("/cart", { method: "DELETE" }); setData(fresh); refreshBadge(); } catch (e) { toast.error(e.message); }
  };

  if (loading) return <Loader />;
  const isEmpty = !data || data.items.length === 0;

  return (
    <div className="animate-fade-in-up max-w-3xl mx-auto">
      <PageHeader
        title={`سلة المشتريات${data && data.items_count > 0 ? ` (${data.items_count})` : ""}`}
        right={!isEmpty ? <button data-testid="cart-clear" onClick={clearAll} className="w-10 h-10 rounded-xl bg-[#FFE5EA] text-[#FF3D5A] flex items-center justify-center"><Trash2 size={18} /></button> : null}
      />
      {isEmpty ? (
        <EmptyState icon={ShoppingCart} title="سلتك فارغة" action={<button data-testid="empty-browse" onClick={() => navigate("/")} className="bg-[#FF3D5A] text-white font-black px-6 py-3 rounded-2xl">تصفّح المنتجات</button>} />
      ) : (
        <>
          <div className="space-y-4">
            {data.groups.map((g) => (
              <div key={g.seller_id} className="bg-white rounded-2xl border border-[#E8EAF2] p-4">
                <button onClick={() => navigate(`/seller/${g.seller_id}`)} className="flex items-center gap-2 pb-3 border-b border-[#F0F2FA] w-full">
                  <Store size={18} className="text-[#FF3D5A]" />
                  <span className="font-black text-[#161629] flex-1 text-start">{g.seller_name}</span>
                  <span className="text-xs text-[#6B7280] font-bold">{g.items_count} منتج</span>
                </button>
                {g.items.map((it) => {
                  const lineTotal = it.unit_price * it.quantity;
                  return (
                    <div key={it.item_id} className="flex gap-3 py-3 border-b border-[#F0F2FA]">
                      <button onClick={() => navigate(`/product/${it.product_id}`)} className="w-16 h-16 rounded-xl bg-[#F7F8FE] overflow-hidden shrink-0">
                        {it.product_image && <img src={it.product_image} alt="" className="w-full h-full object-cover" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-[#161629] text-sm line-clamp-2">{it.product_name}</p>
                        {(it.color || it.size) && (
                          <div className="flex gap-1.5 mt-1">
                            {it.color && <span className="text-[10px] bg-[#F7F8FE] px-1.5 py-0.5 rounded font-bold text-[#6B7280]">لون: {it.color}</span>}
                            {it.size && <span className="text-[10px] bg-[#F7F8FE] px-1.5 py-0.5 rounded font-bold text-[#6B7280]">حجم: {it.size}</span>}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <span className="font-black text-[#FF3D5A]">{lineTotal.toFixed(2)} د.ل</span>
                          {it.discount_percent > 0 && <span className="text-[10px] text-[#9CA3AF] line-through font-bold">{(it.base_price * it.quantity).toFixed(2)}</span>}
                        </div>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <button data-testid={`qty-up-${it.item_id}`} disabled={busy === it.item_id || it.quantity >= it.in_stock_quantity} onClick={() => updateQty(it, 1)} className="w-7 h-7 rounded-lg bg-[#F7F8FE] flex items-center justify-center"><Plus size={14} /></button>
                        <span className="font-black text-sm">{busy === it.item_id ? "…" : it.quantity}</span>
                        <button data-testid={`qty-down-${it.item_id}`} disabled={busy === it.item_id} onClick={() => it.quantity > 1 ? updateQty(it, -1) : remove(it)} className="w-7 h-7 rounded-lg bg-[#F7F8FE] flex items-center justify-center">{it.quantity > 1 ? <Minus size={14} /> : <Trash2 size={14} className="text-[#FF3D5A]" />}</button>
                      </div>
                    </div>
                  );
                })}
                <div className="flex justify-between items-center pt-3">
                  <span className="text-sm text-[#6B7280] font-bold">المجموع الفرعي</span>
                  <span className="font-black text-[#161629]">{g.subtotal.toFixed(2)} د.ل</span>
                </div>
                <button data-testid={`checkout-group-${g.seller_id}`} onClick={() => navigate(`/cart/checkout?seller_id=${g.seller_id}`)} className="w-full flex items-center justify-center gap-2 mt-3 py-2.5 rounded-xl bg-[#FFE5EA] border border-[#FF3D5A] text-[#FF3D5A] font-black text-sm">
                  <BadgeCheck size={18} /> الدفع لهذا المتجر فقط
                </button>
              </div>
            ))}
          </div>
          <div className="sticky bottom-20 lg:bottom-4 mt-4 flex items-center gap-4 bg-white p-4 rounded-2xl border border-[#E8EAF2] brand-shadow-md">
            <div className="flex-1">
              <p className="text-xs text-[#6B7280]">الإجمالي ({data.unique_sellers} متجر)</p>
              <p className="text-2xl font-black text-[#FF3D5A]">{data.total.toFixed(2)} د.ل</p>
            </div>
            <button data-testid="checkout-all" onClick={() => navigate("/cart/checkout")} className="flex items-center gap-2 h-14 px-6 rounded-2xl bg-[#FF3D5A] text-white font-black"><CreditCard size={20} /> إتمام الشراء</button>
          </div>
        </>
      )}
    </div>
  );
}
